/*
* Initial variant image (snippets/variant-initial-image.liquid)
*
* Landing on a variant URL - ?variant=47360711655595, the shape our EDM links
* take - selects the variant correctly but leaves the gallery on the first
* image. Story dispatches the right event, just too early to be heard:
*
*   customElements.define('product-form', ...) runs while theme.js is
*   evaluating, which upgrades the <product-form> already in the DOM. Its
*   constructor reaches updateProductImage(), which dispatches
*   'theme:image:change' on the slideshow. The listener for that event is
*   bound by Media, and Media is not constructed until load('*') runs inside
*   theme.js's DOMContentLoaded handler. The init event has no listener and is
*   dropped, so Flickity settles on slide 0.
*
* Every later change works, because by then the listener exists. So all this
* has to do is send the same event once more, after Media is up.
*
* "After Media is up" is checked, not timed. A first version of this file
* leaned on deferred-script order - theme.js in <head>, this in the body, so
* our DOMContentLoaded handler would run after theme.js's - and it failed on
* the preview theme, because deferred scripts execute at readyState
* "interactive", not "loading". The DOMContentLoaded branch was never taken,
* this ran during script evaluation, and the event fell into exactly the same
* void as Story's own. So rather than reason about ordering, wait for the
* Flickity instance to appear on the slideshow: Media registers it in
* createSlider() and binds the listener at the end of that same synchronous
* block, which makes it a direct signal that the listener exists. It is
* present at mobile widths too, where watchCSS leaves Flickity inactive but
* still constructed.
*
* The event is re-sent rather than the slide moved by hand, so desktop
* (Flickity select, thumbnails synced) and mobile (horizontal scroll) both go
* through the path that already works.
*/

(function (root) {
  'use strict';

  /* --- Pure core -------------------------------------------------------
     No DOM. Unit-tested in test/variant-initial-image.test.cjs. */

  /* The media the gallery should be showing for a variant, or null when there
     is nothing to say - unknown variant, or a variant with no image of its
     own. Null leaves the gallery where it is rather than forcing it to the
     first image, which is what a variant without its own shot should show. */
  function targetMediaId(product, variantId) {
    if (!product || !product.variants) return null;

    var id = Number(variantId);
    if (!id) return null;

    for (var i = 0; i < product.variants.length; i++) {
      var variant = product.variants[i];
      if (variant.id !== id) continue;
      return variant.featured_media ? variant.featured_media.id : null;
    }

    return null;
  }

  var api = {targetMediaId: targetMediaId};

  /* Exported for the Node test runner; harmless in a browser. */
  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }

  /* Node stops here - everything below needs a document. */
  if (typeof document === 'undefined') return;

  /* Rendered once per product section, but the snippet may end up on a page
     twice; a second execution must not double-bind. */
  if (root.GlowcoVariantInitialImage) return;
  root.GlowcoVariantInitialImage = api;

  /* --- DOM layer -------------------------------------------------------- */

  function productOf(form) {
    var node = form.querySelector('[data-product-json]');
    try {
      return node ? JSON.parse(node.innerHTML) : null;
    } catch (error) {
      return null;
    }
  }

  /* The same lookup updateProductImage uses, so we resolve to the same
     element it did. Absent when the product has one media, since Liquid then
     renders a plain .product__slides--single with nothing to drive. */
  function slideshowOf(form, handle) {
    var section = form.closest('[data-section-id]');
    if (!section) return null;
    return section.querySelector('[data-product-slideshow="' + handle + '"]');
  }

  /* Media registers the Flickity instance on the slideshow in createSlider()
     and binds the theme:image:change listener at the end of that same
     synchronous block, so the instance being there means the listener is too.
     Sending the event before this point is what the original bug does. */
  function listenerBound(slideshow) {
    var flickity = root.themeVendor && root.themeVendor.FlickityFade;
    return !!(flickity && flickity.data && flickity.data(slideshow));
  }

  function belowMedium() {
    var sizes = root.theme && root.theme.sizes;
    return root.innerWidth < ((sizes && sizes.medium) || 768);
  }

  /* Below the medium breakpoint Story's handler scrolls the slideshow with
     behavior:'smooth', and on this slideshow that stalls about 40px in and
     never arrives - reproduced on the live theme too, which is why changing
     colour has never moved the mobile gallery. The same scroll with the
     stylesheet's smooth overridden lands exactly. So mobile is scrolled here
     instead of through the event, which also avoids leaving a half-finished
     smooth scroll running against it.

     Assigning scrollLeft under an inline scroll-behavior is used rather than
     behavior:'instant' so this does not depend on that keyword. */
  function scrollToSlide(slideshow, slide) {
    var previous = slideshow.style.scrollBehavior;
    slideshow.style.scrollBehavior = 'auto';
    slideshow.scrollLeft = slide.offsetLeft;
    slideshow.style.scrollBehavior = previous;
  }

  /* 'sent'    - the gallery was pointed at the image
     'waiting' - there is an image to show, but Media is not up yet
     'nothing' - no image to show, so stop asking */
  function sync(form) {
    var product = productOf(form);
    if (!product) return 'nothing';

    var idInput = form.querySelector('[name="id"]');
    if (!idInput) return 'nothing';

    var mediaId = targetMediaId(product, idInput.value);
    if (mediaId === null) return 'nothing';

    var slideshow = slideshowOf(form, product.handle);
    if (!slideshow) return 'nothing';

    /* Story's handler assumes the slide is there; a media id with no slide
       would leave it selecting index -1. */
    var slide = slideshow.querySelector('[data-media-id="' + mediaId + '"]');
    if (!slide) return 'nothing';

    /* The Flickity instance means Media has finished init(), so on desktop the
       listener is bound and on mobile the scroller is set up and offsetLeft is
       measurable. */
    if (!listenerBound(slideshow)) return 'waiting';

    if (belowMedium()) {
      scrollToSlide(slideshow, slide);
      return 'sent';
    }

    slideshow.dispatchEvent(new CustomEvent('theme:image:change', {detail: {id: mediaId}}));
    return 'sent';
  }

  /* True while at least one form still has an image to show and nobody to
     show it to. */
  function syncAll() {
    var waiting = false;

    Array.prototype.forEach.call(
      document.querySelectorAll('[data-product-form]'),
      function (form) {
        if (sync(form) === 'waiting') waiting = true;
      }
    );

    return waiting;
  }

  /* Poll until Media is up. That is a frame or two away - it happens in
     theme.js's DOMContentLoaded handler, and we start during script
     evaluation - so this lands as early as it can without depending on the
     order two DOMContentLoaded handlers happen to run in. The deadline stops
     it from spinning on a product where Media never initialises. */
  var DEADLINE_MS = 5000;

  function attempt(deadline) {
    if (!syncAll()) return;
    if (Date.now() > deadline) return;
    root.requestAnimationFrame(function () {
      attempt(deadline);
    });
  }

  function start() {
    attempt(Date.now() + DEADLINE_MS);
  }

  /* The load case is only half of it. Because that same scroll stalls,
     tapping a colour has never moved the mobile gallery either - true on the
     live theme with none of this file on the page. Story fires
     theme:variant:change after every change and it bubbles, so make the same
     correction from here. Delegated on document, which also covers forms
     injected later (upsell modals). Desktop is left alone: Story's handler
     works there, and its fade is nicer than a jump. */
  document.addEventListener('theme:variant:change', function (event) {
    if (!belowMedium()) return;

    var variant = event.detail && event.detail.variant;
    if (!variant || !variant.featured_media) return;

    /* theme.js fires this on the <product-form> element; [data-product-form]
       is the <form> inside it. */
    var form = event.target.querySelector
      ? event.target.querySelector('[data-product-form]')
      : null;
    if (!form) return;

    var product = productOf(form);
    if (!product) return;

    var slideshow = slideshowOf(form, product.handle);
    if (!slideshow) return;

    var slide = slideshow.querySelector('[data-media-id="' + variant.featured_media.id + '"]');
    if (slide) scrollToSlide(slideshow, slide);
  });

  document.addEventListener('shopify:section:load', start);

  /* rAF is paused in a background tab, so the poll above can time out on a
     page opened in one - which is exactly how an EDM link often opens. load
     fires either way, and by then Media is long up. */
  root.addEventListener('load', start);

  start();
})(typeof globalThis !== 'undefined' ? globalThis : this);
